import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import {
  IClientMatchResult,
  IDialogInboxAddress,
  IDialogInboxClient,
  IParticipantDecision,
} from '../../Interfaces/IDialogInbox';
import { useSearchClients } from '../../hooks/reactQuerys/DialogInboxQueries';
import { useSearchPostcodes } from '../../hooks/reactQuerys/PostcodeQueries';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { IPostcode } from '../../Interfaces/IPostcode';
import { ACTION_LABEL, formatGermanDate, labelForField } from '../i18n';
import { MergeClientPreviewDialog } from './MergeClientPreviewDialog';

const normCity = (s?: string | null) =>
  (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

interface Props {
  idDialogInbox: number;
  participant: IDialogInboxClient;
  decision?: IParticipantDecision;
  onDecide: (decision: IParticipantDecision) => void;
  onResetDecision?: (tempId: string) => void;
}

type MergeFieldKind = 'scalar' | 'date' | 'address';
type MergeField = { key: string; label: string; kind: MergeFieldKind };

// Felder die beim Merge pro Feld entschieden werden können. Es werden ALLE
// Dialog-Daten berücksichtigt (Ticket #1034) — inkl. Telefon und Anschrift.
const MERGE_FIELDS: MergeField[] = [
  { key: 'FirstName', label: 'Vorname', kind: 'scalar' },
  { key: 'LastName', label: 'Nachname', kind: 'scalar' },
  { key: 'SurName', label: 'Geburtsname', kind: 'scalar' },
  { key: 'Email', label: 'E-Mail', kind: 'scalar' },
  { key: 'PhoneNumber', label: 'Telefon', kind: 'scalar' },
  { key: 'Birthdate', label: 'Geburtsdatum', kind: 'date' },
  { key: 'Birthplace', label: 'Geburtsort', kind: 'scalar' },
  { key: 'TaxNumber', label: 'Steuer-Nr.', kind: 'scalar' },
  { key: 'RegisterNumber', label: 'Handelsregister-Nr.', kind: 'scalar' },
  { key: 'address', label: 'Anschrift', kind: 'address' },
];

const addrToString = (a?: IDialogInboxAddress): string => {
  if (!a) return '';
  const street = [a.Street, a.StreetNr].filter(Boolean).join(' ');
  const city = [a.Postcode, a.City].filter(Boolean).join(' ');
  return [street, city].filter(Boolean).join(', ').trim();
};

// Normalisierter Anzeige-/Vergleichswert eines Merge-Feldes (immer String).
const fieldDisplay = (
  obj: IDialogInboxClient | IClientMatchResult,
  field: MergeField
): string => {
  if (field.kind === 'address') {
    return addrToString((obj as { address?: IDialogInboxAddress }).address);
  }
  const raw = (obj as unknown as Record<string, unknown>)[field.key];
  if (field.kind === 'date' && typeof raw === 'string') {
    return formatGermanDate(raw);
  }
  return (raw ?? '').toString().trim();
};

// Rohwert, der bei Übernahme aus dem Dialog in mergeData geschrieben wird
// (für 'address' das ganze Adress-Objekt — das Backend hängt es an die
// AddressArray-/ContactInformation-Pipeline an).
const fieldRawForMerge = (
  participant: IDialogInboxClient,
  field: MergeField
): unknown => {
  if (field.kind === 'address') return participant.address;
  return (participant as unknown as Record<string, unknown>)[field.key];
};

const formatDisplayName = (c: IDialogInboxClient | IClientMatchResult) => {
  const first =
    (c as IClientMatchResult).FirstName ?? (c as IDialogInboxClient).FirstName;
  const last =
    (c as IClientMatchResult).LastName ?? (c as IDialogInboxClient).LastName;
  return [first, last].filter(Boolean).join(' ') || '(ohne Namen)';
};

export const ClientMatchSection: React.FC<Props> = ({
  idDialogInbox,
  participant,
  decision,
  onDecide,
  onResetDecision,
}) => {
  const { searchClients, isSearchingClients } = useSearchClients();
  const [matches, setMatches] = useState<IClientMatchResult[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<IClientMatchResult | null>(
    null
  );
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeChoice, setMergeChoice] = useState<
    Record<string, 'dialog' | 'existing'>
  >({});
  // Gesetzt, sobald die Feld-Auswahl bestätigt wurde: öffnet die
  // Gesamtvorschau (Ticket #1024) als eigenen Bestätigungsschritt.
  const [previewMergeData, setPreviewMergeData] = useState<Record<
    string,
    unknown
  > | null>(null);

  // PLZ-Absicherung der Beteiligten-Adresse (nur wenn eine Adresse übermittelt
  // wurde): Dialog-PLZ eindeutig auflösen (exakter PLZ+Stadt-Treffer), sonst
  // wählt der Mitarbeiter die korrekte PLZ manuell.
  const dialogPlz = (participant.address?.Postcode ?? '').trim();
  const dialogCity = (participant.address?.City ?? '').trim();
  const hasAddress = !!(participant.address?.Street || dialogPlz || dialogCity);
  const [postcodeObject, setPostcodeObject] = useState<IPostcode | null>(null);
  const autoResolvedRef = useRef(false);
  const { postcodes } = useSearchPostcodes(dialogPlz);
  useEffect(() => {
    if (autoResolvedRef.current) return;
    if (!dialogPlz || !postcodes) return;
    autoResolvedRef.current = true;
    const exact = postcodes.filter(p => p.Postcode === dialogPlz);
    const pick = dialogCity
      ? exact.find(p => normCity(p.City) === normCity(dialogCity))
      : exact.length === 1
        ? exact[0]
        : undefined;
    if (pick) setPostcodeObject(pick);
  }, [postcodes, dialogPlz, dialogCity]);

  // Beim Wechsel des Beteiligten Auto-Suche starten
  useEffect(() => {
    setSelectedMatch(null);
    setMergeMode(false);
    setMergeChoice({});
    setPreviewMergeData(null);
    setPostcodeObject(null);
    autoResolvedRef.current = false;

    const isCompany =
      participant.idLegalClientType_hint === 'Juristische Person';
    searchClients({
      idDialogInbox,
      query: {
        FirstName: isCompany ? undefined : participant.FirstName,
        LastName: isCompany ? undefined : participant.LastName,
        Email: participant.Email,
        Company: isCompany ? participant.LastName : undefined,
      },
    })
      .then(res => setMatches(res.matches))
      .catch(() => setMatches([]));
  }, [idDialogInbox, participant, searchClients]);

  const handleUse = () => {
    if (!selectedMatch) return;
    onDecide({
      tempId: participant.tempId,
      action: 'use',
      idLegalClient: selectedMatch.idLegalClient,
    });
  };

  const dialogEmailLower = (participant.Email ?? '').trim().toLowerCase();
  const emailDuplicate = dialogEmailLower
    ? (matches.find(
        m => (m.Email ?? '').trim().toLowerCase() === dialogEmailLower
      ) ?? null)
    : null;

  const handleCreate = () => {
    if (emailDuplicate) {
      // Schutz: bei doppelter E-Mail nicht neu anlegen — Backend wuerde
      // das ebenfalls blocken. Hinweis im UI ist ausreichend.
      return;
    }
    onDecide({
      tempId: participant.tempId,
      action: 'create',
      idPostcode: postcodeObject?.idPostcode ?? undefined,
    });
  };

  const handleIgnore = () => {
    onDecide({ tempId: participant.tempId, action: 'ignore' });
  };

  const handleStartMerge = () => {
    if (!selectedMatch) return;
    setMergeMode(true);
    const initial: Record<string, 'dialog' | 'existing'> = {};
    for (const f of MERGE_FIELDS) {
      const dialogVal = fieldDisplay(participant, f);
      const existingVal = fieldDisplay(selectedMatch, f);
      if (!dialogVal) {
        initial[f.key] = 'existing';
      } else if (!existingVal) {
        initial[f.key] = 'dialog';
      } else {
        initial[f.key] = dialogVal !== existingVal ? 'dialog' : 'existing';
      }
    }
    setMergeChoice(initial);
  };

  const handleConfirmMerge = () => {
    if (!selectedMatch) return;
    const mergeData: Record<string, unknown> = {};
    for (const f of MERGE_FIELDS) {
      const dialogVal = fieldDisplay(participant, f);
      const existingVal = fieldDisplay(selectedMatch, f);
      const dialogEmpty = !dialogVal;
      const existingEmpty = !existingVal;
      // Dialog-Wert uebernehmen, wenn:
      //  - der User es im Radio gewaehlt hat (conflict-case), oder
      //  - der Bestand leer war (auto-fill).
      const shouldUseDialog =
        mergeChoice[f.key] === 'dialog' || (existingEmpty && !dialogEmpty);
      if (shouldUseDialog && !dialogEmpty) {
        mergeData[f.key] = fieldRawForMerge(participant, f);
      }
    }
    // Statt direkt zu übernehmen: Gesamtvorschau öffnen (Ticket #1024).
    // mergeMode bleibt aktiv, damit „Zurück" wieder zur Feld-Auswahl führt.
    setPreviewMergeData(mergeData);
  };

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Mandantensuche für {formatDisplayName(participant)}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant='caption' color='text.secondary'>
            Angaben aus dem Dialog
          </Typography>
          <Typography variant='body2'>
            {formatDisplayName(participant)}
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            {[
              participant.Email,
              participant.Birthdate
                ? `geb. ${formatGermanDate(participant.Birthdate)}`
                : null,
              participant.idBusinessForm_hint,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
        <Box>
          {decision && (
            <Alert
              severity='info'
              action={
                onResetDecision ? (
                  <Button
                    color='inherit'
                    size='small'
                    onClick={() => onResetDecision(participant.tempId)}
                  >
                    Zurücksetzen
                  </Button>
                ) : undefined
              }
            >
              Vorgemerkt: <strong>{ACTION_LABEL[decision.action]}</strong>
              {decision.idLegalClient
                ? ` (Mandant Nr. ${decision.idLegalClient})`
                : ''}
              <Box
                component='span'
                sx={{ display: 'block', mt: 0.5, fontSize: 12 }}
              >
                Wird erst beim Klick auf <em>Vorgang erstellen</em> angelegt.
              </Box>
            </Alert>
          )}
        </Box>
      </Box>

      {hasAddress && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='caption' color='text.secondary'>
            Anschrift aus dem Dialog: {addrToString(participant.address) || '—'}
          </Typography>
          {postcodeObject ? (
            <Alert severity='success' sx={{ mt: 0.5, mb: 1 }}>
              PLZ/Ort erkannt:{' '}
              <strong>
                {postcodeObject.Postcode} {postcodeObject.City}
              </strong>
              . Wird bei „{ACTION_LABEL.create}"/„{ACTION_LABEL.merge}"
              verwendet — bei Bedarf unten korrigieren.
            </Alert>
          ) : (
            <Alert severity='warning' sx={{ mt: 0.5, mb: 1 }}>
              Die PLZ/den Ort aus dem Dialog (
              <strong>
                {participant.address?.Postcode || '—'}{' '}
                {participant.address?.City || ''}
              </strong>
              ) konnte nicht eindeutig zugeordnet werden. Bitte wählen Sie die
              korrekte PLZ.
            </Alert>
          )}
          <PostcodeSearch
            postcodeObject={postcodeObject}
            updatePostcodeObject={v => setPostcodeObject(v ?? null)}
          />
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {emailDuplicate && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Die E-Mail-Adresse <strong>{participant.Email}</strong> ist bereits
          dem Mandanten <strong>{formatDisplayName(emailDuplicate)}</strong>{' '}
          (Nr. {emailDuplicate.idLegalClient}) zugeordnet. Eine E-Mail darf
          nicht doppelt vergeben werden — bitte „{ACTION_LABEL.use}" oder „
          {ACTION_LABEL.merge}" wählen.
        </Alert>
      )}

      <Typography variant='subtitle2' sx={{ mb: 1 }}>
        Mandantensuche ({matches.length})
      </Typography>
      {isSearchingClients ? (
        <CircularProgress size={20} />
      ) : matches.length === 0 ? (
        <Alert severity='warning'>
          Keine passenden Mandanten gefunden – bitte „Mandant neu anlegen"
          wählen.
        </Alert>
      ) : (
        <Stack
          spacing={1}
          sx={{
            mb: 2,
            // Bei vielen Treffern scrollbar machen, damit die Auswahl
            // übersichtlich bleibt.
            maxHeight: 320,
            overflowY: 'auto',
            pr: 0.5,
          }}
        >
          {matches.map(m => (
            <Paper
              key={m.idLegalClient}
              variant='outlined'
              onClick={() => {
                setSelectedMatch(m);
                setMergeMode(false);
              }}
              sx={{
                p: 1.5,
                cursor: 'pointer',
                bgcolor:
                  selectedMatch?.idLegalClient === m.idLegalClient
                    ? 'action.selected'
                    : 'background.paper',
              }}
            >
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                sx={{ flexWrap: 'wrap' }}
              >
                <Typography variant='body2' sx={{ flexGrow: 1 }}>
                  {formatDisplayName(m)} · {m.Email ?? '–'}
                </Typography>
                <Chip
                  size='small'
                  label={`Übereinstimmung ${m.score}%`}
                  color={m.score >= 75 ? 'success' : 'default'}
                />
                {m.matchedFields.map(f => (
                  <Chip
                    key={f}
                    size='small'
                    label={labelForField(f)}
                    variant='outlined'
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {mergeMode &&
        selectedMatch &&
        (() => {
          // Werte pro Feld holen + Anzeige-Format
          const fieldRows = MERGE_FIELDS.map(f => {
            const dialogVal = fieldDisplay(participant, f);
            const existingVal = fieldDisplay(selectedMatch, f);
            // Konflikt nur wenn beide Werte vorhanden sind UND sich unterscheiden.
            // Wenn der Dialog einen Wert liefert und der Bestand leer ist,
            // wird der Dialog-Wert automatisch übernommen — kein Radio nötig.
            const dialogEmpty = !dialogVal;
            const existingEmpty = !existingVal;
            const isConflict =
              !dialogEmpty && !existingEmpty && dialogVal !== existingVal;
            return {
              field: f,
              dialogVal,
              existingVal,
              isConflict,
              dialogEmpty,
              existingEmpty,
            };
          });

          const conflictRows = fieldRows.filter(r => r.isConflict);
          const fillRows = fieldRows.filter(
            r => !r.isConflict && r.existingEmpty && !r.dialogEmpty
          );

          return (
            <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
              {conflictRows.length === 0 && fillRows.length === 0 ? (
                <Alert severity='success'>
                  Alle Felder stimmen überein — beim Zusammenführen wird der
                  bestehende Mandant unverändert übernommen.
                </Alert>
              ) : (
                <>
                  {conflictRows.length > 0 && (
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      Bitte wählen Sie pro Feld den zu übernehmenden Wert:
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '160px 1fr',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    {conflictRows.map(r => {
                      // Mehrwertige Kontaktfelder (Telefon/Anschrift) werden vom
                      // Backend additiv angehaengt — daher heisst die Wahl hier
                      // klar "Beide behalten" vs "Nur Bestand" statt "Aus Dialog"
                      // (das wuerde faelschlich Ersetzen suggerieren). #1092
                      const isAppend =
                        r.field.kind === 'address' ||
                        r.field.key === 'PhoneNumber';
                      return (
                        <React.Fragment key={r.field.key}>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {r.field.label}
                          </Typography>
                          <RadioGroup
                            row
                            value={mergeChoice[r.field.key] ?? 'existing'}
                            onChange={(_, v) =>
                              setMergeChoice(prev => ({
                                ...prev,
                                [r.field.key]: v as 'dialog' | 'existing',
                              }))
                            }
                          >
                            <FormControlLabel
                              value='dialog'
                              control={<Radio size='small' />}
                              label={
                                isAppend
                                  ? `Beide behalten (zusätzlich: ${r.dialogVal || '–'})`
                                  : `Aus Dialog: ${r.dialogVal || '–'}`
                              }
                            />
                            <FormControlLabel
                              value='existing'
                              control={<Radio size='small' />}
                              label={
                                isAppend
                                  ? `Nur Bestand: ${r.existingVal || '–'}`
                                  : `Bestehend: ${r.existingVal || '–'}`
                              }
                            />
                          </RadioGroup>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                  {fillRows.length > 0 && (
                    <Box sx={{ mt: conflictRows.length > 0 ? 2 : 0 }}>
                      <Typography variant='caption' color='text.secondary'>
                        Folgende Felder sind beim bestehenden Mandanten leer und
                        werden automatisch aus dem Dialog übernommen:
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {fillRows.map(r => (
                          <Chip
                            key={r.field.key}
                            size='small'
                            label={`${r.field.label}: ${r.dialogVal}`}
                            variant='outlined'
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}
              <Stack direction='row' spacing={1} sx={{ mt: 2 }}>
                <Button variant='contained' onClick={handleConfirmMerge}>
                  Zusammenführung bestätigen
                </Button>
                <Button onClick={() => setMergeMode(false)}>Abbrechen</Button>
              </Stack>
            </Paper>
          );
        })()}

      <Stack
        direction='row'
        spacing={1}
        flexWrap='wrap'
        sx={{
          // Aktions-Buttons immer sichtbar halten (#1091) — bei vielen
          // Dubletten-Vorschlaegen musste man sonst erst durchscrollen.
          position: 'sticky',
          bottom: 0,
          zIndex: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          pt: 1,
          mt: 1,
        }}
      >
        <Button
          variant='contained'
          color='success'
          disabled={!selectedMatch || mergeMode}
          onClick={handleUse}
        >
          {ACTION_LABEL.use}
        </Button>
        <Button
          variant='outlined'
          color='info'
          disabled={!selectedMatch || mergeMode}
          onClick={handleStartMerge}
        >
          {ACTION_LABEL.merge}
        </Button>
        <Button
          variant='outlined'
          color='success'
          disabled={!!emailDuplicate}
          title={
            emailDuplicate
              ? 'E-Mail ist bereits vergeben — Neu anlegen nicht moeglich'
              : undefined
          }
          onClick={handleCreate}
        >
          {ACTION_LABEL.create}
        </Button>
        <Button variant='outlined' color='warning' onClick={handleIgnore}>
          {ACTION_LABEL.ignore}
        </Button>
      </Stack>

      {previewMergeData && selectedMatch && (
        <MergeClientPreviewDialog
          idLegalClient={selectedMatch.idLegalClient}
          mergeData={previewMergeData}
          onBack={() => setPreviewMergeData(null)}
          onConfirm={() => {
            onDecide({
              tempId: participant.tempId,
              action: 'merge',
              idLegalClient: selectedMatch.idLegalClient,
              mergeData: previewMergeData,
              idPostcode: postcodeObject?.idPostcode ?? undefined,
            });
            setPreviewMergeData(null);
            setMergeMode(false);
          }}
        />
      )}
    </Paper>
  );
};
